import * as React from 'react'
import { inject, observer } from 'mobx-react'
import CSVReader from 'react-csv-reader'
import { Select, message, Alert, Spin } from 'antd'
import { API } from '@getgreenline/homi-shared'
import { InventoryLogType } from '@getgreenline/inventory/build/models/inventoryLog'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  onSubmit: () => void
}

interface State {
  importStatus: null | 'importing' | 'completed'
  locationId?: number
  importProgress: {
    successSKUs: string[]
    failedSKUs: string[]
    total: number
  }
}

export interface UploadFile {
  lastModified: number
  lastModifiedDate: Date
  name: string
  preview: string
  size: number
  type: string
  webkitRelativePath: string
}

export interface DropzoneFile {
  lastModified: number
  lastModifiedDate: Date
  name: string
  originFileObj: UploadFile
  percent: string
  size: number
  type: string
  status: string
  uid: string
}

@inject('currentCompanyStore')
@observer
export class Import extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      importStatus: null,
      importProgress: {
        successSKUs: [],
        failedSKUs: [],
        total: 0,
      },
    }
  }

  async onFileLoad(data: string[]) {
    const company = this.props.currentCompanyStore!.company!

    if (!this.state.locationId) {
      return message.error('Please select a location first')
    }

    if (data.length === 0) {
      return message.error('No data in csv')
    }

    // Find indexes for relevant columns
    // If can't find, values will be -1
    const headers = data[0]
    const skuIndex = headers.indexOf('SKU')
    const lowStockQuantityIndex = headers.indexOf('Low Stock Quantity')
    const restockQuantityIndex = headers.indexOf('Restock Quantity')

    if (skuIndex < 0 || lowStockQuantityIndex < 0 || restockQuantityIndex < 0) {
      return message.error('Missing one of: SKU, Low Stock Quantity, or Restock Quantity columns')
    }

    if (window.confirm('Start your import?')) {
      const dataWithoutHeader = data.slice(1, data.length)

      this.setState({
        importStatus: 'importing',
        importProgress: {
          successSKUs: [],
          failedSKUs: [],
          total: dataWithoutHeader.length,
        },
      })

      const failedSKUs = []
      const successSKUs = []

      for (const product of dataWithoutHeader) {
        const sku = product[skuIndex]

        if (!sku) {
          continue
        }

        const lowStockQuantity =
          product[lowStockQuantityIndex] === '' ? null : parseInt(product[lowStockQuantityIndex])
        const restockQuantity =
          product[restockQuantityIndex] === '' ? null : parseInt(product[restockQuantityIndex])
        const { multiLocationInventory } = await API.getInventoryByMultipleLocations(company.id, {
          sku: sku,
          searchQuery: '',
          selectedCategoryId: undefined,
          selectedSupplierId: undefined,
          locationIds: [this.state.locationId],
          offset: 0,
          limit: 1,
          hideEmptyQuantities: false,
        })

        if (multiLocationInventory.length === 1) {
          const inventory = multiLocationInventory[0]
          const updateObject = {
            mode: 'add' as const,
            updates: [
              {
                productId: inventory.product.id,
                quantity: 0,
                lowStockQuantity: lowStockQuantity,
                restockQuantity: restockQuantity,
                batchInfo: {
                  isBatchTracked: inventory.product.isBatchTracked,
                },
                type: 'audit' as InventoryLogType,
              },
            ],
          }
          await this.props.currentCompanyStore!.updateBulkInventory(
            this.state.locationId,
            updateObject,
          )
          successSKUs.push(sku)
        } else {
          // No matching SKU
          failedSKUs.push(sku)
        }

        this.setState({
          importProgress: {
            successSKUs: successSKUs,
            failedSKUs: failedSKUs,
            total: dataWithoutHeader.length,
          },
        })
      }

      this.setState({ importStatus: 'completed' })
      this.props.onSubmit()
      message.success('Low stock quantities successfully updated')
    } else {
      message.error('Import cancelled')
    }
  }

  render() {
    const locations = this.props.currentCompanyStore!.locations || []

    return (
      <div>
        <Alert
          message={
            <>
              <p>Upload a CSV with the following headers (case sensitive):</p>
              <ul>
                <li>SKU</li>
                <li>Low Stock Quantity</li>
                <li>Restock Quantity</li>
              </ul>
              <div className='mt-2'>
                Leaving a csv cell empty will result in the low stock/restock quantities being
                cleared.
              </div>
            </>
          }
        />

        <div className='mt-2 mb-2'>
          <Select<number>
            style={{ width: '100%' }}
            placeholder='Select location (required)'
            value={this.state.locationId}
            onChange={(locationId) => this.setState({ locationId })}
          >
            {locations.map((location) => (
              <Select.Option key={location.id} value={location.id}>
                {location.name}
              </Select.Option>
            ))}
          </Select>
        </div>

        {this.state.importStatus !== 'importing' && !!this.state.locationId && (
          <CSVReader
            cssClass='csv-input mb-2'
            onFileLoaded={(data: any) => {
              this.onFileLoad(data)
            }}
            onError={(error: any) => {
              message.error('There was an error parsing this .csv file')
            }}
          />
        )}

        {this.state.importStatus === 'importing' && (
          <div>
            <Spin active style={{ marginRight: 10 }} />
            Progress:{' '}
            {`${
              this.state.importProgress.failedSKUs.length +
              this.state.importProgress.successSKUs.length
            }`}{' '}
            / {this.state.importProgress.total}
          </div>
        )}

        {this.state.importStatus === 'completed' && (
          <>
            <Alert
              type='success'
              message='Successfully updated'
              style={{ marginBottom: 5 }}
              description={
                <ul className='m-0'>
                  {this.state.importProgress.successSKUs.map((sku, index) => {
                    return <li key={index}>{sku}</li>
                  })}
                </ul>
              }
            />
            {this.state.importProgress.failedSKUs.length > 0 && (
              <Alert
                type='error'
                message='Failed to update'
                description={
                  <ul className='m-0'>
                    {this.state.importProgress.failedSKUs.map((sku, index) => {
                      return <li key={index}>{sku}</li>
                    })}
                  </ul>
                }
              />
            )}
          </>
        )}
      </div>
    )
  }
}
